import type { SavedReport } from '@clockwise/clients/report-definitions';
import type { ReportLabel, ReportLabelTarget } from '@clockwise/clients/report-labels';
import { Link } from '@tanstack/react-router';
import {
  CopyPlus,
  MoreHorizontal,
  Pencil,
  Share2,
  Star,
  TextCursorInput,
  Trash2,
} from 'lucide-react';
import { ShareBadge } from '@/components/custom/share-badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { LabelPicker } from './label-picker';
import { BUILT_IN_REPORTS, type LibraryEntry, updatedAgo } from './library-model';
import { starTargetId, useToggleLabelItem } from './use-library';

export interface RowActions {
  onShare: (report: SavedReport) => void;
  onDuplicate: (report: SavedReport) => void;
  onRename: (report: SavedReport) => void;
  onDelete: (report: SavedReport) => void;
}

interface Props extends RowActions {
  entry: LibraryEntry;
  labels: readonly ReportLabel[];
  /** The search, for highlighting. */
  q: string;
  canEdit: boolean;
  canShare: boolean;
}

/**
 * One report in the list.
 *
 * Labels sit INLINE after the name — they are part of what the report is
 * called in this list, and on the far right they read as metadata nobody
 * scans. The label button and the star appear on hover (and on focus, for the
 * keyboard); a starred report keeps its star showing.
 */
export function ReportRow({
  entry,
  labels,
  q,
  canEdit,
  canShare,
  onShare,
  onDuplicate,
  onRename,
  onDelete,
}: Props) {
  const toggle = useToggleLabelItem();
  const starId = starTargetId(labels);
  const starred = labels.some((l) => l.kind === 'starred' && entry.labelIds.has(l.id));
  const target: ReportLabelTarget = entry.saved
    ? { reportId: entry.saved.id }
    : { builtinKey: entry.builtinKey as NonNullable<LibraryEntry['builtinKey']> };

  // Personal labels first, then standard — yours are the ones you put there,
  // and the order matches the rail's. Starred shows as the star, not a chip.
  const chips = [
    ...labels.filter((l) => l.kind === 'personal' && entry.labelIds.has(l.id)),
    ...labels.filter((l) => l.kind === 'standard' && entry.labelIds.has(l.id)),
  ];
  const report = entry.saved;
  const builtin = BUILT_IN_REPORTS.find((b) => b.key === entry.builtinKey);

  return (
    <li
      className="group flex items-start gap-3 border-b px-3 py-3 transition-colors last:border-b-0 hover:bg-muted/40 focus-within:bg-muted/40 light:hover:bg-row-hover light:focus-within:bg-row-hover"
      data-testid="report-row"
    >
      <button
        type="button"
        aria-label={starred ? `Unstar ${entry.name}` : `Star ${entry.name}`}
        aria-pressed={starred}
        onClick={() => toggle.mutate({ labelId: starId, target, on: !starred })}
        className={cn(
          'mt-0.5 rounded-sm text-muted-foreground transition-opacity hover:text-foreground',
          starred
            ? 'text-amber-500 opacity-100 hover:text-amber-600'
            : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 focus-visible:opacity-100',
        )}
      >
        <Star className={cn('size-4', starred && 'fill-current')} aria-hidden />
      </button>

      <div className="min-w-0 flex-1">
        <LabelPicker
          reportName={entry.name}
          target={target}
          labels={labels}
          current={entry.labelIds}
          hasLabels={chips.length > 0}
        >
          {report ? (
            <Link
              to="/reports/custom/$reportId"
              params={{ reportId: report.id }}
              className="mr-0.5 font-medium hover:underline"
            >
              <Highlight text={entry.name} q={q} />
            </Link>
          ) : builtin ? (
            <Link to={builtin.to} className="mr-0.5 font-medium hover:underline">
              <Highlight text={entry.name} q={q} />
            </Link>
          ) : null}
          {chips.map((l) => (
            <span
              key={l.id}
              className={cn(
                'inline-flex h-5 items-center rounded border px-1.5 font-medium text-muted-foreground text-xs',
                // Same shape and size for both kinds — only the fill tells
                // yours (shaded) from the company's (outlined), as in the rail.
                l.kind === 'personal'
                  ? 'border-transparent bg-muted'
                  : 'border-border bg-background',
              )}
            >
              <Highlight text={l.name} q={q} />
            </span>
          ))}
        </LabelPicker>
        <p className="mt-1 truncate text-muted-foreground text-sm">
          {/* A recipient decides whether to run a report from this line
              (CR-3.7), so its absence is named rather than left blank. */}
          <Highlight text={entry.description ?? 'No description.'} q={q} />
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-3 text-muted-foreground text-xs">
        {report ? (
          <>
            {report.owned && <ShareBadge owned shares={report.shares} />}
            <OwnerAvatar owned={report.owned} name={report.ownerName} />
            <time
              dateTime={report.updatedAt}
              title={`Updated ${new Date(report.updatedAt).toLocaleString()}`}
              className="w-14 text-right tabular-nums"
            >
              {updatedAgo(report.updatedAt)}
            </time>
          </>
        ) : (
          <span className="pr-0.5">Built-in</span>
        )}
        {report ? (
          <RowMenu
            report={report}
            canEdit={canEdit}
            canShare={canShare}
            onShare={onShare}
            onDuplicate={onDuplicate}
            onRename={onRename}
            onDelete={onDelete}
          />
        ) : (
          // Keeps the right edge aligned with rows that have a menu.
          <span className="size-7" aria-hidden />
        )}
      </div>
    </li>
  );
}

/**
 * What can be done to a saved report from the list. Only what THIS caller may
 * do is offered — an owner edits, shares, renames and deletes; anyone who may
 * build reports may keep a copy. The server refuses the rest regardless.
 *
 * Edit opens the report, whose setup column is open by default for a builder —
 * the same page the name links to, offered here because "where do I change
 * this report" is a question people ask of the menu, not of the name.
 */
function RowMenu({
  report,
  canEdit,
  canShare,
  onShare,
  onDuplicate,
  onRename,
  onDelete,
}: RowActions & { report: SavedReport; canEdit: boolean; canShare: boolean }) {
  const share = report.owned && canShare;
  const manage = report.owned && canEdit;
  if (!share && !canEdit) return <span className="size-7" aria-hidden />;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
          aria-label={`Actions for ${report.name}`}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {manage && (
          <DropdownMenuItem asChild>
            <Link to="/reports/custom/$reportId" params={{ reportId: report.id }}>
              <Pencil className="size-4" aria-hidden />
              Edit
            </Link>
          </DropdownMenuItem>
        )}
        {share && (
          <DropdownMenuItem onSelect={() => onShare(report)}>
            <Share2 className="size-4" aria-hidden />
            Share…
          </DropdownMenuItem>
        )}
        {canEdit && (
          <DropdownMenuItem onSelect={() => onDuplicate(report)}>
            <CopyPlus className="size-4" aria-hidden />
            Save a copy
          </DropdownMenuItem>
        )}
        {manage && (
          <>
            <DropdownMenuItem onSelect={() => onRename(report)}>
              <TextCursorInput className="size-4" aria-hidden />
              Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => onDelete(report)}>
              <Trash2 className="size-4" aria-hidden />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Whose report it is, at a glance: "You", or the owner's initials. The full
 * name is the tooltip and the accessible label — initials alone say nothing
 * to a screen reader. An owner whose name could not be resolved shows "?",
 * never "You": only `owned` may say the reader owns it.
 */
function OwnerAvatar({ owned, name }: { owned: boolean; name: string | null }) {
  const label = owned ? 'Owner: you' : name ? `Owner: ${name}` : 'Owner unknown';
  const initials = owned
    ? 'You'
    : name
      ? name
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w[0]?.toUpperCase())
          .join('')
      : '?';
  return (
    <span
      title={owned ? 'You' : (name ?? 'Unknown owner')}
      role="img"
      aria-label={label}
      className="inline-flex size-6 items-center justify-center rounded-full bg-muted font-semibold text-[10px] text-muted-foreground uppercase"
    >
      {initials}
    </span>
  );
}

/** `text` with every case-insensitive occurrence of `q` marked. */
export function Highlight({ text, q }: { text: string; q: string }) {
  const needle = q.trim().toLowerCase();
  if (!needle) return <>{text}</>;
  const parts: React.ReactNode[] = [];
  const lower = text.toLowerCase();
  let at = 0;
  let i = lower.indexOf(needle);
  while (i !== -1) {
    if (i > at) parts.push(text.slice(at, i));
    parts.push(
      <mark key={i} className="rounded-sm bg-amber-100 px-0.5 text-inherit dark:bg-amber-900/40">
        {text.slice(i, i + needle.length)}
      </mark>,
    );
    at = i + needle.length;
    i = lower.indexOf(needle, at);
  }
  if (at < text.length) parts.push(text.slice(at));
  return <>{parts}</>;
}
